// Mapeador FaturaExtraida → payload plano da tabela `faturas` (contrato dos robôs n8n).
// Reproduz as fórmulas do fluxo GPT legado (nó "Valida e normaliza payload" do Robô
// Assinatura / expressões inline do Robô Manual) sobre o modelo normalizado do parser
// determinístico. Escolha de coluna validada contra executions reais dos robôs:
//   - tarifas "sem imposto" (bandeira, consumo não comp., consumo SCEE) ← itens[].tarifa_unit
//   - tarifas "com imposto" (valor_tarifa_distribuidora, scee_c_imposto, injeção GD) ← preco_unit_com_tributos
// TS puro (sem Deno): roda em `node --test` igual ao fatura-parser.

import type { FaturaExtraida, ItemFatura } from '../fatura-parser/types.ts';

// ─────────────── helpers com a MESMA semântica do fluxo legado ───────────────

/** "1.234,56" → 1234.56 | "0,022475" → 0.022475 | "64.170" → 64170 | lixo → null */
export function parseBR(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  let s = String(v).replace(/[^\d.,-]/g, '');
  if (s === '' || !/\d/.test(s)) return null;
  if (s.includes(',')) {
    s = s.replace(/\./g, '').replace(',', '.');
  } else if (/^-?\d{1,3}(\.\d{3})+$/.test(s)) {
    s = s.replace(/\./g, '');
  }
  const n = parseFloat(s);
  return Number.isNaN(n) ? null : n;
}

/** Campo numérico ausente vira 0 (paridade com o fluxo antigo). */
const num = (v: unknown): number => {
  const n = parseBR(v);
  return n === null ? 0 : n;
};

/** "SALDO KWH" do bloco SCEE → número. Grupo B vem simples ("2.407,05"); Grupo A vem
 *  por posto tarifário ("P=152,72, FP=291.780,99, HR=0,00") e o saldo total é a soma —
 *  parseBR sozinho devolveria 0 nesse formato. */
export function parseSaldoKwh(v: unknown): number {
  const s = String(v ?? '').trim();
  if (!s) return 0;
  if (/[A-Za-z]+\s*=/.test(s)) {
    const total = [...s.matchAll(/[A-Za-z]+\s*=\s*(-?[\d.]*\d(?:,\d+)?)/g)]
      .reduce((acc, m) => acc + (parseBR(m[1]) ?? 0), 0);
    return parseFloat(total.toFixed(2));
  }
  return num(s);
}

/** ''/null/'null' → null; senão a string trimada. */
const str = (v: unknown): string | null => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s === '' || s.toLowerCase() === 'null' ? null : s;
};

/** Só aceita data ISO completa (o parser já emite ISO); qualquer outra coisa vira null. */
const isoDate = (v: unknown): string | null => {
  const s = str(v);
  return s && /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
};

/** "06/2026" → "JUN/2026" (guard do Robô Manual; o parser normalmente já emite "JUN/2026"). */
const MESES = ['', 'JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
export function mesReferencia(contaMes: string): string {
  const raw = (contaMes || '').trim().toUpperCase();
  const m = raw.match(/^(\d{2})\/(\d{4})$/);
  if (!m) return raw;
  return (MESES[parseInt(m[1], 10)] || m[1]) + '/' + m[2];
}

/** end_numero legado: só o token rotulado "N. 1234" conta como número do imóvel
 *  ("Q. 37"/"L. 1"/"APART - 4" NÃO são número). O logradouro fica integral. */
export function splitLogradouro(logradouro: string): { end_logradouro: string | null; end_numero: string | null } {
  const s = (logradouro || '').trim().replace(/\s+/g, ' ');
  if (!s) return { end_logradouro: null, end_numero: null };
  const m = s.match(/\bN[º°.]\s*(\d+\w*)/i);
  return { end_logradouro: s, end_numero: m ? m[1].toUpperCase() : 'S/N' };
}

// ─────────────── identificação de itens da fatura ───────────────

const isInjecao = (it: ItemFatura) => /INJE[ÇC][ÃA]O\s+SCEE/i.test(it.descricao);
const isParcInjet = (it: ItemFatura) => /PARC\s*INJET/i.test(it.descricao);
// Layout de GD II com "BENEFÍCIO TARIFÁRIO" (2026): o OCR troca o rótulo "PARC INJET
// S/DESC" pela etiqueta da GD ("GD II 2"). Só essa linha fica com a etiqueta sozinha e
// valor positivo — a injeção vem negativa. Caso real: fatura 11231, UC 152499501254.
const isParcInjetSemRotulo = (it: ItemFatura) =>
  /^\s*GD\s+I{1,2}(\s+\d+)?\s*$/i.test(it.descricao) && num(it.valor) > 0;
// No mesmo layout a linha da injeção às vezes perde o rótulo inteiro: sobra a única
// linha de FORNECIMENTO com quantidade, tarifa e valor negativo.
const isInjecaoSemRotulo = (it: ItemFatura) =>
  !it.descricao.trim() && num(it.qtd) > 0 && num(it.valor) < 0 && str(it.tarifa_unit) !== null;
const isIlum = (it: ItemFatura) => /ILUM/i.test(it.descricao);
const isNaoIsenta = (it: ItemFatura) => /N[ÃA]O\s+ISENTA/i.test(it.descricao);
const isBandeiraAmarela = (it: ItemFatura) => /ADC\s+BANDEIRA\s+AMARELA/i.test(it.descricao);
const isBandeiraVermelha = (it: ItemFatura) => /ADC\s+BANDEIRA\s+VERMELHA/i.test(it.descricao);
const isConsumoNaoComp = (it: ItemFatura) =>
  /CONSUMO\s+N[ÃA]O\s+COMPENSADO/i.test(it.descricao) ||
  // fatura sem GD: a linha é só "CONSUMO" / "CONSUMO KWh" / "CONSUMO FP" (mesma tarifa cheia)
  (/^CONSUMO\b/i.test(it.descricao.trim()) && !/SCEE/i.test(it.descricao));
const isConsumoScee = (it: ItemFatura) => /CONSUMO\s+SCEE/i.test(it.descricao);

// ─────────────── saída ───────────────

export interface PayloadFaturas {
  [k: string]: string | number | boolean | null;
}

export interface RoboMeta {
  uc_com_mascara: string;        // UC como impressa na fatura (com pontos) — header x-uc-codigo do Manual
  conta_mes: string;             // "JUN/2026" — headers x-conta-mes / x-mes-referencia
  tipo_pdf_fatura: 'espelho' | 'definitiva';
}

export function mapearPayloadFaturas(f: FaturaExtraida): { payload: PayloadFaturas; meta: RoboMeta } {
  const cab = f.cabecalho;
  const itens = f.itens || [];
  const forn = itens.filter((i) => i.grupo_secao !== 'ITENS FINANCEIROS');
  const financeiros = itens.filter((i) => i.grupo_secao === 'ITENS FINANCEIROS');

  const injecoesRotuladas = forn.filter(isInjecao);
  const injecoes = injecoesRotuladas.length > 0 ? injecoesRotuladas : forn.filter(isInjecaoSemRotulo);
  const injecaoTotalKwh = injecoes.reduce((s, i) => s + Math.abs(num(i.qtd)), 0);

  // CONSUMO SCEE / CONSUMO NÃO COMPENSADO: por rótulo; fallback p/ linhas de FORNECIMENTO
  // que perderam o rótulo no OCR (descricao vazia, mas com qtd/tarifas/valor) — caso real
  // do corpus. A linha órfã com a MESMA qtd da injeção é o CONSUMO SCEE; a órfã restante
  // (qtd da bandeira / consumo não compensado) é o CONSUMO NÃO COMPENSADO.
  const orfas = forn.filter(
    (i) => !i.descricao.trim() && num(i.qtd) > 0 && num(i.valor) > 0 && str(i.tarifa_unit) !== null,
  );
  let consumoScee = forn.find(isConsumoScee);
  if (!consumoScee && injecoes.length > 0) {
    consumoScee = orfas.find((i) => Math.abs(num(i.qtd) - Math.abs(num(injecoes[0].qtd))) < 0.01);
  }
  let consumoNaoComp = forn.find(isConsumoNaoComp);
  if (!consumoNaoComp) {
    consumoNaoComp = orfas.find((i) => i !== consumoScee);
  }
  const bandAmarela = forn.find(isBandeiraAmarela);
  const bandVermelha = forn.find(isBandeiraVermelha);
  const parcInjet = forn.find(isParcInjet) || forn.find(isParcInjetSemRotulo);
  const gd1 = injecoes.find((i) => i.tipo_gd === 'GD I') || injecoes[0];
  const gd2 = injecoes.find((i) => i.tipo_gd === 'GD II');

  // Ilum. pública fica em coluna própria; TODOS os demais itens financeiros (multa, juros,
  // correção, 2ª via, descontos negativos...) somam em outros_multas — é o que faz o
  // final_total do FaturaValidationModal fechar com o total impresso na fatura.
  const ilum = itens.find(isIlum);
  const outrosMultas = financeiros
    .filter((i) => !isIlum(i))
    .reduce((s, i) => s + num(i.valor), 0);

  // ENERGIA COMP NÃO ISENTA pode vir 1× por UG — soma (fica no bloco FORNECIMENTO).
  const naoIsenta = forn.filter(isNaoIsenta).reduce((s, i) => s + num(i.valor), 0);

  const trib = (tipo: string) => f.tributos.find((t) => t.tipo === tipo);
  const scee0 = f.scee[0];

  const endSplit = splitLogradouro(cab.end_logradouro);
  const isEspelho = /-old\b/.test(f.meta.parser_version);

  const payload: PayloadFaturas = {
    uc_codigo: (cab.unidade_consumidora || '').replace(/[.\-\s]/g, '').replace(/^0+/, ''),
    cliente_nome: str(cab.nome_cliente),
    // só dígitos — o banco normaliza assim de todo jeito
    cliente_cpf_cnpj: str((cab.cpf_cnpj || '').replace(/\D/g, '')),
    tipo_ligacao: str(cab.tipo_fornecimento),
    // legado grava o rótulo completo como impresso ("B OPT B3 COMERCIAL - ...")
    classificacao: str(cab.classificacao_completa || [cab.grupo, cab.sub_grupo, cab.classificacao].filter(Boolean).join(' ')),
    mes_referencia: mesReferencia(cab.conta_mes),

    data_apresentacao: isoDate(cab.apresentacao),
    data_vencimento: isoDate(cab.vencimento),
    leitura_anterior: isoDate(cab.data_leitura_anterior),
    leitura_atual: isoDate(cab.data_leitura_atual),
    proxima_leitura: isoDate(cab.proxima_leitura),

    valor_fatura_distribuidora: num(cab.total_pagar),
    consumo_ativo_kwh: num(cab.consumo_kwh),
    energia_injetada_gd1_kwh: parseFloat(injecaoTotalKwh.toFixed(2)),
    // "Saldo do mês" (o que sobrou deste ciclo) ≠ "Saldo Acumulado Vigor" (o SALDO KWH
    // impresso no bloco SCEE, que é o estoque total de créditos da UC na distribuidora).
    saldo_acumulado_kwh: num(scee0?.excedente_recebido_kwh) - num(f.saldos.credito_recebido),
    saldo_ugs_vigor: parseSaldoKwh(f.saldos.saldo),
    credito_recebido: num(f.saldos.credito_recebido),
    geracao_ciclo: str(scee0?.ciclo_mes),
    cadastro_rateio: num(scee0?.percent_rateio),

    // preço COM tributos: o app recalcula valor_adc_bandeira = (consumo − inj) × tarifa_bandeira,
    // e o valor impresso na fatura é qtd × preço c/ tributos (0,024569 e não 0,018850)
    tarifa_bandeira: parseFloat((num(bandAmarela?.preco_unit_com_tributos) + num(bandVermelha?.preco_unit_com_tributos)).toFixed(6)),
    tarifa_s_imp_consumo_nao_comp: num(consumoNaoComp?.tarifa_unit),
    valor_tarifa_distribuidora: num(consumoNaoComp?.preco_unit_com_tributos),
    tarifa_consumo_s_imposto: num(consumoScee?.tarifa_unit),
    consumo_scee_tarifa_c_imposto: num(consumoScee?.preco_unit_com_tributos),
    tarifa_injecao_gd1: num(gd1?.preco_unit_com_tributos),
    tarifa_injecao_gd2: num(gd2?.preco_unit_com_tributos),
    tarifa_parc_injet_s_desc_gd2: num(parcInjet?.preco_unit_com_tributos),
    ilum_publica: num(ilum?.valor),
    energia_nao_isenta: parseFloat(naoIsenta.toFixed(2)),
    outros_multas: parseFloat(outrosMultas.toFixed(2)),

    icms: num(trib('ICMS')?.aliquota),
    pis: num(trib('PIS_PASEP')?.aliquota),
    cofins: num(trib('COFINS')?.aliquota),

    end_logradouro: endSplit.end_logradouro,
    end_numero: endSplit.end_numero,
    end_bairro: str(cab.end_bairro),
    end_cep: str(cab.end_cep),
    end_cidade: str(cab.end_cidade),
    end_uf: str(cab.end_uf),

    numero_medidor: str(cab.numero_medidor),
    tem_reaviso_vencimento: !!cab.tem_reaviso,
    reaviso_vencimento_texto: str(cab.reaviso_texto),
    tipo_pdf_fatura: isEspelho ? 'espelho' : 'definitiva',

    // Linha digitável/PIX da PRÓPRIA fatura da Equatorial (não confundir com os
    // campos do boleto Vigor, preenchidos depois pela emissão) — usados pelo
    // repasse automático do boleto único (trg_enfileirar_pagamento_equatorial).
    linha_digitavel_equatorial: str(cab.codigo_barras),
    pix_copia_cola_equatorial: str(cab.pix_copia_cola),
    debito_automatico_equatorial: !!cab.debito_automatico,
  };

  // Slots UG 1..5: listas SCEE × INJEÇÃO independentes (paridade com o legado).
  for (let g = 1; g <= 5; g++) {
    const s = f.scee[g - 1];
    const inj = injecoes[g - 1];
    payload[`unidade_geradora_${g}`] = str(s?.uc_geradora);
    payload[`geracao_unidade_geradora_${g}`] = num(s?.geracao_ciclo_kwh);
    payload[`excedente_recebido_unidade_geradora_${g}`] = num(s?.excedente_recebido_kwh);
    payload[`descricao_ug_${g}`] = str(inj?.descricao);
    payload[`injecao_kwh_ug_${g}`] = Math.abs(num(inj?.qtd));
  }

  const meta: RoboMeta = {
    uc_com_mascara: (cab.unidade_consumidora || '').trim(),
    conta_mes: mesReferencia(cab.conta_mes),
    tipo_pdf_fatura: isEspelho ? 'espelho' : 'definitiva',
  };

  return { payload, meta };
}
